import { FC, useEffect, useRef, useState } from 'react';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import { Link, useParams } from 'react-router-dom';
import {
  RecordKey,
  RecordValue,
  RecordValueWithSensograms,
  SENSOR_NATURE,
  getFullRecord,
  getFullRecords,
  getRecord,
  updateRecordBoundaries,
  updateRecordDescription,
  updateRecordHumidityCalibrantReferenceKey,
  updateRecordName,
  withIdb,
} from '../../../services/cache/idb';
import { FlexCol, FlexRow, Paper } from '../../../components/common/common';
import uPlot from 'uplot';
import { SingleSensogramFigure } from '../../../components/widgets/Graph/recordSensogram';
import { DEFAULT_PLOT_DECIMATED_FPS, DEFAULT_PLOTLY_MARGIN } from '../../../utils/constants/constants';
import { Breadcrumb, Button, Col, Input, InputRef, Row, Space, Tooltip, Typography, message as antdMessage } from 'antd';
import { SingleSignatureFigure } from '../../../components/widgets/Graph/recordSignature';
import { BoxPlotOutlined, BoxPlotTwoTone, BugOutlined, BugTwoTone, HomeOutlined, SaveOutlined } from '@ant-design/icons';
import OdorResult, { OdorZone } from './OdorResult';
import { loadHumidityCalibrant } from '../../../services/cache/localStorage';
import { ColormapWidget } from '../../../components/widgets/Colormap/colormapWidget';
import Plot from 'react-plotly.js';
import { getColormap } from '../../../components/analysis/colormap';
import { performAnalysis } from '../../../components/analysis/pca';
import { getSignaturesFigure } from '../../../components/analysis/figures';
import { computeSignature, DEFAULT_SIGNATURE_PROCESSING_PARAMS, handleNormalisation } from '../../../components/analysis/signature';
import { euclideanDistance, mean } from '../../../components/analysis/utils';
import { NON_STANDARD_PEPTIDE_COLOR_MAP, PEPTIDE_COLOR_MAP_VDW, useMediaQuery } from '../../../utils/helpers/utils';
import { pipeline } from 'stream';
import { PipelineType, ProcessingPipelineParams } from '../../../components/analysis/definitions';
import { SignatureWithSpotgrid } from '../../../types/types';
import { DEFAULT_ODOR_ZONES } from '../Records/OdorResult';
import { computeDeltaSensor } from '../../../components/analysis/compute';
// import { intensityScalingFactor } from '../../SensePage/Recording/OdorResultsModal';

type signature = number[]; // 1xM matrix

enum ZoneSelectorType {
  Baseline,
  Analyte,
}

export const Record: FC = () => {
  const { recordId } = useParams<{ recordId: string }>();
  const [record, setRecord] = useState<RecordValueWithSensograms | null>(null);

  const targetRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const nameInputRef = useRef<InputRef>(null);
  const descriptionInputRef = useRef<InputRef>(null);
  const humidityCalibrationRefKeyInputRef = useRef<InputRef>(null);

  const [zoneSelectorType, setZoneSelectorType] = useState<ZoneSelectorType>(ZoneSelectorType.Analyte);
  const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
  const [shouldAggregate, setShouldAggregate] = useState<boolean>(true);

  // Wound analysis
  const [intensity, setIntensity] = useState<number>(0);
  const [patientId, setPatientId] = useState<string>('');
  const [date, setDate] = useState<number>(Date.now());
  const [recordKeys, setRecordKeys] = useState<RecordKey[]>([]);
  const [humidityDistance, setHumidityDistance] = useState<number | null>(null);
  const [remainingIntensityAfterHumidityCorrection, setRemainingIntensityAfterHumidityCorrection] = useState<number | null>(null);
  const [humidityCalibrant, setHumidityCalibrant] = useState<SignatureWithSpotgrid | undefined>(loadHumidityCalibrant());

  const [commonSpotsgrid, setCommonSpotsgrid] = useState<number[]>([]);
  const [HumidityCorrectedRawIntensity, setHumidityCorrectedRawIntensity] = useState<number>(0);

  // build toolings for superpose signature figure : commonSpotsgrid, signatures, labels, cmap

  const [cmap, setCmap] = useState<Record<string, [number, number, number]>>({});
  const [signaturesPlotlyData, setSignaturesPlotlyData] = useState<Plotly.Data[]>([]);
  const [signaturesPlotlyLayout, setSignaturesPlotlyLayout] = useState<Partial<Plotly.Layout>>({});
  const [records, setRecords] = useState<RecordValueWithSensograms[]>([]);
  const [signatures, setSignatures] = useState<number[][]>([]);
  const [intensities, setIntensities] = useState<number[]>([]);
  const isMobile = useMediaQuery();

  const [humidityCorrectedPlotlyData, setHumidityCorrectedPlotlyData] = useState<Plotly.Data[]>([]);
  const [humidityCorrectedPlotlyLayout, setHumidityCorrectedPlotlyLayout] = useState<Partial<Plotly.Layout>>({});

  const THRESHOLD_RAD = 1; //

  const thresholdShape: Partial<Plotly.Shape> = {
    type: 'line',
    xref: 'paper',
    yref: 'y',
    x0: 0,
    x1: 1,
    y0: THRESHOLD_RAD,
    y1: THRESHOLD_RAD,
    line: {
      color: 'rgba(140, 140, 140, 0.6)',
      width: 1.2,
      dash: 'dash',
    },
  };

  const thresholdAnnotation: Partial<Plotly.Annotations> = {
    xref: 'paper',
    yref: 'y',
    x: 1,
    y: THRESHOLD_RAD,
    xanchor: 'right',
    yanchor: 'bottom',
    text: `${THRESHOLD_RAD.toFixed(2)} rad`,
    showarrow: false,
    font: {
      size: 10,
      color: 'rgba(140, 140, 140, 0.85)',
      family: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
  };

  // Example of a custom scaling you could pass in
  const CUSTOM_ODOR_ZONES: OdorZone[] = [
    { label: 'No odor', min: 0, max: 10, color: '#42A5F5', flex: 2 },
    { label: 'Low odor', min: 10, max: 20, color: '#26C6DA', flex: 2 },
    { label: 'Medium odor', min: 20, max: 30, color: '#F5A623', flex: 4 },
    { label: 'High odor', min: 30, max: 100, color: '#E91E8C', flex: 2 },
  ];

  // Effect 1: fetch the main record
  useEffect(() => {
    getFullRecord(recordId).then((_record) => {
      setRecord(_record);

      console.log('fetching humidity record');
      let { normalisation: norm, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;

      if (_record.humidityCalibrantRefKey !== undefined) {
        getFullRecord(_record.humidityCalibrantRefKey).then((_water_record) => {
          let [_signature, _spotsgrid1d] = computeSignature(_water_record, processingParamNoNorm);
          let deltaHumidity = computeDeltaSensor(_water_record, SENSOR_NATURE.Humidity);
          let deltaTemperature = computeDeltaSensor(_water_record, SENSOR_NATURE.Temperature);
          if (_record.humidityCalibrantRefKey !== undefined) {
            setHumidityCalibrant({
              recordKey: _record.humidityCalibrantRefKey,
              signature: _signature,
              spotsgrid: _spotsgrid1d,
              deltaHumidity: deltaHumidity,
              deltaTemperature: deltaTemperature,
            });
          }
        });
      }
    });
    console.log('humidity calibrant is ', humidityCalibrant?.recordKey);
  }, [recordId]);

  // Effect 2: once record + humidityCalibrant are both ready, fetch all records in one shot
  useEffect(() => {
    if (!humidityCalibrant?.recordKey || !record?.key) return;

    getFullRecords([humidityCalibrant.recordKey, record.key]).then((_records) => {
      _records.sort((a, b) => b.absoluteTimestamp - a.absoluteTimestamp);
      setRecords(_records);
    });
    console.log('test should not rerender');
  }, [record, humidityCalibrant]);

  // Effect 3: compute signatures + intensities once records are ready
  useEffect(() => {
    if (records.length === 0) return;

    try {
      let signatures: number[][] = [];
      let intensities: number[] = [];
      let spotsgrids1d: number[][] = [];

      let { normalisation: normParam, ...processingParamNoNorm } = DEFAULT_SIGNATURE_PROCESSING_PARAMS;

      for (let record_item of records) {
        let [_signature, _spotsgrid1d] = computeSignature(record_item, processingParamNoNorm);

        const normalizedSignature = normParam !== undefined ? handleNormalisation(_signature, _spotsgrid1d, normParam) : _signature;

        signatures.push(normalizedSignature);
        spotsgrids1d.push(_spotsgrid1d);
        intensities.push(mean(_signature));
      }

      // Validate all signatures have the same length
      const signatureLength = signatures[0].length;
      for (let sign of signatures) {
        if (sign.length !== signatureLength) throw new Error('signatures have different lengths');
      }

      // Validate all spotsgrids are identical
      const commonSpotsgrid = spotsgrids1d[0];
      for (let spotsgrid1d of spotsgrids1d) {
        if (spotsgrid1d.length !== commonSpotsgrid.length) throw new Error('spotsgrids have different lengths');
        for (let i = 0; i < spotsgrid1d.length; i++) {
          if (spotsgrid1d[i] !== commonSpotsgrid[i]) throw new Error('spotsgrids are not the same');
        }
      }

      // Compute plotly figure while we're here — avoids a 4th chained effect
      let labels = records.map((r) => (r.name ? r.name : 'unnamed'));
      let uniqueLabels = Array.from(new Set(labels));
      let cmap = getColormap(uniqueLabels);
      let { data: signaturesPlotlyData, layout: signaturesPlotlyLayout } = getSignaturesFigure(commonSpotsgrid, signatures, labels, cmap);

      signaturesPlotlyLayout.margin = DEFAULT_PLOTLY_MARGIN;
      signaturesPlotlyLayout.showlegend = false;

      // compute distance to humidity
      let distance = euclideanDistance(signatures[0], signatures[1]);
      let SCALING_FACTOR = 100;

      // compute raw corrected signature
      if (record !== null) {
        let [_signature, _spotsgrid1d] = computeSignature(record, processingParamNoNorm);
        // colors
        let color: string[] = [];
        spotsgrids1d[0].forEach((spotInt) => {
          if (PEPTIDE_COLOR_MAP_VDW[spotInt] !== undefined) {
            color.push(PEPTIDE_COLOR_MAP_VDW[spotInt]);
          } else {
            color.push(NON_STANDARD_PEPTIDE_COLOR_MAP[spotInt % NON_STANDARD_PEPTIDE_COLOR_MAP.length]);
          }
        });
        // humidity corrections
        // delta H processing

        // we need to compute delta H
        const baselineBounds = [record.baselineStart, record.baselineEnd];
        const analyteBounds = [record.analyteStart, record.analyteEnd];
        let baselineHumidity = mean(record.humiditySeries.slice(...baselineBounds));
        let analyteHumidity = mean(record.humiditySeries.slice(...analyteBounds));
        let deltaH = analyteHumidity - baselineHumidity;
        // console.log('zones are', baselineBounds, analyteBounds);
        let finalProcessingParameters: ProcessingPipelineParams = {
          pipelineType: PipelineType.Classic,
          aggregation: processingParamNoNorm.aggregation,
        };
        if (humidityCalibrant) {
          // console.log('humidityCalibrant', humidityCalibrant);
          // fetch delta H of the record
          finalProcessingParameters.humidityCorrectionParams = { deltaH: deltaH, calibrant: humidityCalibrant };
        }
        // console.log('finalProcessingparameters is', finalProcessingParameters);
        let [_correctedSignature] = computeSignature(record, finalProcessingParameters);
        // console.log('corrected signature vs signature', _correctedSignature, _signature);
        const plotlyData: Plotly.Data[] = [
          {
            type: 'bar',
            x: commonSpotsgrid.map((p) => '&nbsp;' + p), // same category hack as your real code
            y: _correctedSignature,
            marker: {
              color: color,
            },
            hoverinfo: 'x+y',
            hoveron: 'points',
          },
        ];

        const plotlyLayout: Partial<Plotly.Layout> = {
          title: 'Raw Signature (humidity corrected)',
          xaxis: { title: 'Peptide' },
          yaxis: { title: 'Value' },
          autosize: true,
          shapes: [...(humidityCorrectedPlotlyLayout.shapes ?? []), thresholdShape],
          annotations: [...(humidityCorrectedPlotlyLayout.annotations ?? []), thresholdAnnotation],
        };

        // Batch all state updates in one go — React 18 auto-batches these
        if (record.intensityScalingFactor) {
          setRemainingIntensityAfterHumidityCorrection(record.intensityScalingFactor * Math.max(..._correctedSignature));
        }
        setHumidityDistance(parseInt((SCALING_FACTOR * distance).toFixed(2)));
        setCmap(cmap);
        setCommonSpotsgrid(commonSpotsgrid);
        setSignatures(signatures);
        setIntensities(intensities);
        setSignaturesPlotlyData(signaturesPlotlyData);
        setSignaturesPlotlyLayout(signaturesPlotlyLayout);
        setHumidityCorrectedPlotlyData(plotlyData);
        setHumidityCorrectedPlotlyLayout(plotlyLayout);
      }
    } catch (e: any) {
      console.error(e);
      antdMessage.error(`Error while computing signatures: ${e.message}`);
    }
  }, [records]);

  // // Effect 4: uPlot — independent, only depends on record
  useEffect(() => {
    if (record === null || targetRef.current === null) return;

    let X = record.sensogramTimestamps.map((ts) => ts / 1000);
    let Ys: number[][] = Array.from({ length: 64 }, (_, i) => record.sensogramSeries.map((frame) => frame[i]));

    let opts: uPlot.Options = {
      width: targetRef.current.clientWidth,
      height: targetRef.current.clientHeight,
      legend: { show: false },
      series: [
        {},
        ...Ys.map((_, i) => ({
          show: true,
          stroke: `hsl(${(i * 360) / 64}, 100%, 50%)`,
          label: `S${i}`,
        })),
      ],
    };

    let uplot = new uPlot(opts, [X, ...Ys], targetRef.current);
    uplotRef.current = uplot;

    return () => {
      uplot.destroy();
      uplotRef.current = null;
    };
  }, [record]);

  if (!record) {
    return null;
  }

  return (
    <div
      tabIndex={-1}
      onKeyDown={(e) => {
        console.log('single sensogram figure: keydown', e.key);
        switch (e.key) {
          case 'Shift':
            console.log('shift pressed', zoneSelectorType);
            setZoneSelectorType(ZoneSelectorType.Baseline);
            break;
        }
      }}
      onKeyUp={(e) => {
        console.log('single sensogram figure: keyup', e.key);
        switch (e.key) {
          case 'Shift':
            setZoneSelectorType(ZoneSelectorType.Analyte);
            break;
        }
      }}
    >
      <Paper>
        {/* ── Breadcrumb ── */}
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/home">
                  <HomeOutlined />
                </Link>
              ),
            },
            {
              title: <Link to="/records">Records</Link>,
            },
            {
              title: record.name ? record.name : '—',
            },
          ]}
        />

        {/* ── Toolbar: debug + aggregate toggles ── */}
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'end',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Tooltip overlay="Toggle debug info">
            <Button type="text" size="large" icon={showDebugInfo ? <BugTwoTone /> : <BugOutlined />} onClick={() => setShowDebugInfo(!showDebugInfo)} />
          </Tooltip>
          <Tooltip overlay="Toggle sensor aggregation">
            <Button type="text" size="large" onClick={() => setShouldAggregate(!shouldAggregate)} icon={shouldAggregate ? <BoxPlotTwoTone /> : <BoxPlotOutlined />} />
          </Tooltip>
        </FlexRow>

        {/* ── DEBUG MODE ── */}
        {showDebugInfo && (
          <>
            {/* Name + Description */}
            <FlexRow
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <FlexCol style={{ width: '100%' }}>
                <b>Name:</b>
                <Space.Compact>
                  <Input defaultValue={record.name} placeholder="Name" ref={nameInputRef} />
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    onClick={async () => {
                      if (nameInputRef.current === null || nameInputRef.current.input === null) return;
                      await updateRecordName(record.key, nameInputRef.current.input.value);
                      antdMessage.success('Name updated');
                    }}
                  />
                </Space.Compact>
              </FlexCol>

              <FlexCol style={{ width: '100%' }}>
                <b>Description:</b>
                <Space.Compact>
                  <Input defaultValue={record.description} placeholder="Description" ref={descriptionInputRef} />
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    onClick={async () => {
                      if (descriptionInputRef.current === null || descriptionInputRef.current.input === null) return;
                      await updateRecordDescription(record.key, descriptionInputRef.current.input.value);
                      antdMessage.success('Description updated');
                    }}
                  />
                </Space.Compact>
              </FlexCol>
            </FlexRow>

            {/* humidity calibrant ref key */}
            <FlexRow style={{ width: '100%', padding: 10 }}>
              <FlexCol style={{ width: '100%' }}>
                <b>humidity calibrant reference key:</b>
                <Space.Compact>
                  <Input defaultValue={record.humidityCalibrantRefKey} placeholder="HumidityCalibrantRefKey" ref={humidityCalibrationRefKeyInputRef} />
                  <Button
                    icon={<SaveOutlined />}
                    type="primary"
                    onClick={async () => {
                      if (humidityCalibrationRefKeyInputRef.current === null || humidityCalibrationRefKeyInputRef.current.input === null) return;
                      await updateRecordHumidityCalibrantReferenceKey(record.key, humidityCalibrationRefKeyInputRef.current.input.value);
                      antdMessage.success('Humidity Calibrant reference key updated');
                    }}
                  />
                </Space.Compact>
              </FlexCol>
            </FlexRow>

            {/* Raw JSON */}
            <FlexRow style={{ width: '100%', padding: 10 }}>
              <JsonView data={record} style={defaultStyles} shouldInitiallyExpand={(level) => level < 1} />
            </FlexRow>

            {/* Sensogram + Signature */}
            <Row gutter={[5, 5]}>
              <Col xs={24} lg={16}>
                <div style={{ width: '100%', height: 400 }}>
                  <SingleSensogramFigure
                    record={record}
                    shouldAggregate={shouldAggregate}
                    setBoundaries={async (startIdx, endIdx) => {
                      console.log('single sensogram figure: set boundaries', startIdx, endIdx, zoneSelectorType);
                      let _record = { ...record };
                      if (zoneSelectorType === ZoneSelectorType.Baseline) {
                        _record.baselineStart = startIdx;
                        _record.baselineEnd = endIdx;
                        void (await updateRecordBoundaries(record.key, startIdx, endIdx, undefined, undefined));
                      } else {
                        _record.analyteStart = startIdx;
                        _record.analyteEnd = endIdx;
                        void (await updateRecordBoundaries(record.key, undefined, undefined, startIdx, endIdx));
                      }
                      setRecord(_record);
                    }}
                  />
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div style={{ width: '100%', height: 400 }}>
                  <SingleSignatureFigure record={record} shouldAggregate={shouldAggregate} />
                </div>
              </Col>
            </Row>
            <Row gutter={[10, 10]} align={'stretch'}>
              <Col xs={24} lg={24}>
                <ColormapWidget cmap={cmap} />
              </Col>
              <Col
                xs={24}
                lg={12}
                style={{
                  height: 400,
                }}
              >
                <Plot
                  data={signaturesPlotlyData}
                  layout={signaturesPlotlyLayout}
                  config={{
                    staticPlot: isMobile,
                  }}
                  useResizeHandler
                  style={{
                    width: '100%',
                    height: '100%',
                    margin: 'auto',
                  }}
                />
              </Col>
              {humidityDistance !== null && (
                <Col xs={24} lg={24}>
                  <FlexRow style={{ alignItems: 'center', gap: 8 }}>
                    <b>Distance to humidity calibrant:</b>
                    <Input value={humidityDistance.toFixed(2)} readOnly style={{ width: 160 }} />
                  </FlexRow>
                </Col>
              )}
              {humidityDistance !== null && (
                <Col xs={24} lg={24}>
                  <FlexRow style={{ alignItems: 'center', gap: 8 }}>
                    <Plot divId="signature_scatter_plot" debug={true} data={humidityCorrectedPlotlyData} layout={humidityCorrectedPlotlyLayout} useResizeHandler={true} config={{ responsive: true }} style={{ width: '100%', height: '400px' }} />
                  </FlexRow>
                </Col>
              )}
            </Row>

            <FlexRow style={{ justifyContent: 'center', alignItems: 'center', height: 30 }}>Selecting zone: {ZoneSelectorType[zoneSelectorType]}</FlexRow>
          </>
        )}

        {/* ── MAIN DISPLAY: always visible ── */}
        {!showDebugInfo && humidityDistance !== null && remainingIntensityAfterHumidityCorrection !== null && record.name !== undefined && record.absoluteTimestamp !== undefined && (
          <OdorResult intensity={remainingIntensityAfterHumidityCorrection} patientId={record.name} date={record.absoluteTimestamp} zones={DEFAULT_ODOR_ZONES} />
        )}
      </Paper>
    </div>
  );
};
